/**
 * Единая палитра карточки: чтобы микс SKU не превращался в «дискотеку».
 * Движение (motion) остаётся от рамки/волны/FX, цвета подтягиваются к dominant.
 */

export type CosmeticsHarmony =
  | "ember"
  | "magma"
  | "tide"
  | "ice"
  | "storm"
  | "dusk"
  | "forest"
  | "aurora"
  | "steel"
  | "mist"
  | "neutral"

export interface HarmonyPalette {
  id: CosmeticsHarmony
  label: string
  frameColors: readonly [string, string, string]
  accent: string
  waveColor: string
  winrateColor: string
  rowBorderColor: string
  fxPrimary: string
  fxSecondary: string
  fxSpark: string
}

export const HARMONY_PALETTES: Record<CosmeticsHarmony, HarmonyPalette> = {
  ember: {
    id: "ember",
    label: "Жар",
    frameColors: ["#EA580C", "#FACC15", "#7F1D1D"],
    accent: "#FDBA74",
    waveColor: "rgba(251,146,60,0.38)",
    winrateColor: "#FDBA74",
    rowBorderColor: "rgba(249,115,22,0.65)",
    fxPrimary: "#FF6B35",
    fxSecondary: "#F97316",
    fxSpark: "#FDE68A",
  },
  magma: {
    id: "magma",
    label: "Магма",
    frameColors: ["#F87171", "#EA580C", "#450A0A"],
    accent: "#FCA5A5",
    waveColor: "rgba(239,68,68,0.32)",
    winrateColor: "#F87171",
    rowBorderColor: "rgba(239,68,68,0.55)",
    fxPrimary: "#F97316",
    fxSecondary: "#7F1D1D",
    fxSpark: "#FECACA",
  },
  tide: {
    id: "tide",
    label: "Прилив",
    frameColors: ["#22D3EE", "#2DD4BF", "#0E7490"],
    accent: "#67E8F9",
    waveColor: "rgba(34,211,238,0.35)",
    winrateColor: "#67E8F9",
    rowBorderColor: "rgba(34,211,238,0.55)",
    fxPrimary: "#22D3EE",
    fxSecondary: "#0EA5E9",
    fxSpark: "#BAE6FD",
  },
  ice: {
    id: "ice",
    label: "Лёд",
    frameColors: ["#7DD3FC", "#E0F2FE", "#0284C7"],
    accent: "#BAE6FD",
    waveColor: "rgba(125,211,252,0.4)",
    winrateColor: "#BAE6FD",
    rowBorderColor: "rgba(125,211,252,0.55)",
    fxPrimary: "#7DD3FC",
    fxSecondary: "#38BDF8",
    fxSpark: "#E0F2FE",
  },
  storm: {
    id: "storm",
    label: "Гроза",
    frameColors: ["#38BDF8", "#A78BFA", "#1E3A8A"],
    accent: "#C4B5FD",
    waveColor: "rgba(167,139,250,0.4)",
    winrateColor: "#A5B4FC",
    rowBorderColor: "rgba(167,139,250,0.6)",
    fxPrimary: "#A78BFA",
    fxSecondary: "#6366F1",
    fxSpark: "#E0E7FF",
  },
  dusk: {
    id: "dusk",
    label: "Сумерки",
    frameColors: ["#F472B6", "#A78BFA", "#6B21A8"],
    accent: "#F9A8D4",
    waveColor: "rgba(232,121,249,0.35)",
    winrateColor: "#F9A8D4",
    rowBorderColor: "rgba(244,114,182,0.55)",
    fxPrimary: "#E879F9",
    fxSecondary: "#DB2777",
    fxSpark: "#FCE7F3",
  },
  forest: {
    id: "forest",
    label: "Лес",
    frameColors: ["#4ADE80", "#86EFAC", "#166534"],
    accent: "#86EFAC",
    waveColor: "rgba(74,222,128,0.35)",
    winrateColor: "#86EFAC",
    rowBorderColor: "rgba(74,222,128,0.5)",
    fxPrimary: "#65A30D",
    fxSecondary: "#3F6212",
    fxSpark: "#BBF7D0",
  },
  aurora: {
    id: "aurora",
    label: "Сияние",
    frameColors: ["#34D399", "#A78BFA", "#22D3EE"],
    accent: "#6EE7B7",
    waveColor: "rgba(52,211,153,0.35)",
    winrateColor: "#6EE7B7",
    rowBorderColor: "rgba(52,211,153,0.5)",
    fxPrimary: "#2DD4BF",
    fxSecondary: "#8B5CF6",
    fxSpark: "#C4B5FD",
  },
  steel: {
    id: "steel",
    label: "Сталь",
    frameColors: ["#94A3B8", "#CBD5E1", "#64748B"],
    accent: "#E2E8F0",
    waveColor: "rgba(148,163,184,0.28)",
    winrateColor: "#CBD5E1",
    rowBorderColor: "rgba(148,163,184,0.5)",
    fxPrimary: "#94A3B8",
    fxSecondary: "#64748B",
    fxSpark: "#F1F5F9",
  },
  mist: {
    id: "mist",
    label: "Дымка",
    frameColors: ["#E2E8F0", "#94A3B8", "#64748B"],
    accent: "#F8FAFC",
    waveColor: "rgba(248,250,252,0.3)",
    winrateColor: "#E2E8F0",
    rowBorderColor: "rgba(226,232,240,0.4)",
    fxPrimary: "#CBD5E1",
    fxSecondary: "#94A3B8",
    fxSpark: "#FFFFFF",
  },
  neutral: {
    id: "neutral",
    label: "Нейтраль",
    frameColors: ["#F8FAFC", "#CBD5E1", "#64748B"],
    accent: "#FFFFFF",
    waveColor: "rgba(255,255,255,0.22)",
    winrateColor: "#F8FAFC",
    rowBorderColor: "rgba(255,255,255,0.35)",
    fxPrimary: "#E2E8F0",
    fxSecondary: "#94A3B8",
    fxSpark: "#FFFFFF",
  },
}

const COMPATIBLE: Record<CosmeticsHarmony, CosmeticsHarmony[]> = {
  ember: ["ember", "magma", "neutral", "steel", "mist"],
  magma: ["magma", "ember", "neutral", "steel", "mist"],
  tide: ["tide", "ice", "aurora", "neutral", "steel", "mist"],
  ice: ["ice", "tide", "neutral", "steel", "mist"],
  storm: ["storm", "dusk", "aurora", "neutral", "steel", "mist"],
  dusk: ["dusk", "storm", "neutral", "steel", "mist"],
  forest: ["forest", "aurora", "neutral", "steel", "mist"],
  aurora: ["aurora", "tide", "storm", "forest", "neutral", "steel", "mist"],
  steel: [
    "ember",
    "magma",
    "tide",
    "ice",
    "storm",
    "dusk",
    "forest",
    "aurora",
    "steel",
    "mist",
    "neutral",
  ],
  mist: [
    "ember",
    "magma",
    "tide",
    "ice",
    "storm",
    "dusk",
    "forest",
    "aurora",
    "steel",
    "mist",
    "neutral",
  ],
  neutral: [
    "ember",
    "magma",
    "tide",
    "ice",
    "storm",
    "dusk",
    "forest",
    "aurora",
    "steel",
    "mist",
    "neutral",
  ],
}

export function areHarmoniesCompatible(
  a: CosmeticsHarmony | null | undefined,
  b: CosmeticsHarmony | null | undefined
): boolean {
  if (!a || !b) return true
  return COMPATIBLE[a].includes(b)
}

/** Фон задаёт тему; иначе FX; иначе нейтраль. */
export function pickDominantHarmony(
  bgHarmony?: CosmeticsHarmony | null,
  elementFxHarmony?: CosmeticsHarmony | null
): CosmeticsHarmony {
  if (bgHarmony && bgHarmony !== "neutral") return bgHarmony
  if (elementFxHarmony && elementFxHarmony !== "neutral") return elementFxHarmony
  return bgHarmony ?? elementFxHarmony ?? "neutral"
}

export function resolveNickColorForHarmony(
  nickColor: string | null | undefined,
  nickHarmony: CosmeticsHarmony | null | undefined,
  dominant: CosmeticsHarmony
): string | null {
  if (!nickColor) return null
  if (areHarmoniesCompatible(nickHarmony, dominant)) return nickColor
  return HARMONY_PALETTES[dominant].accent
}
